import { Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { PageHead } from '../ui/index.js';
import { useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { EmptyState } from '../components/layout/Loading.js';
import { useGamepadButton } from '../components/focus/useGamepad.js';

export const Route = createFileRoute('/admin')({ component: AdminLayout });

interface AdminTab {
  to: string;
  label: string;
}

/** Every section here is admin only; the API gates all of `/admin` the same way. */
const TABS: AdminTab[] = [
  { to: '/admin', label: 'Overview' },
  { to: '/admin/insights', label: 'Insights' },
  { to: '/admin/games', label: 'Games' },
  { to: '/admin/team', label: 'Team' },
  { to: '/admin/members', label: 'Members' },
  { to: '/admin/roles', label: 'Roles' },
  { to: '/admin/creators', label: 'Creators' },
  { to: '/admin/storage', label: 'Storage' },
  { to: '/admin/webhooks', label: 'Webhooks' },
  { to: '/admin/api-keys', label: 'API keys' },
  { to: '/admin/audit', label: 'Audit log' },
];

/**
 * The tab a path belongs to: the longest `to` it sits under.
 *
 * Longest rather than first, because every section is under `/admin` and Overview would
 * otherwise claim all of them. This is what keeps Games lit while a game's editor or its build
 * screen is open, which are both `/admin/games/...`.
 */
function activeTab(pathname: string): string {
  return TABS.reduce(
    (best, tab) =>
      (pathname === tab.to || pathname.startsWith(`${tab.to}/`)) && tab.to.length > best.length
        ? tab.to
        : best,
    '',
  );
}

function AdminLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { isAdmin } = useAccess();

  const active = activeTab(pathname);

  const move = (delta: number) => {
    const index = TABS.findIndex((tab) => tab.to === active);
    const next = TABS[(Math.max(0, index) + delta + TABS.length) % TABS.length]!;
    void navigate({ to: next.to });
  };
  useGamepadButton('lb', () => move(-1));
  useGamepadButton('rb', () => move(1));

  if (!isAdmin) return <EmptyState title="You cannot do that." />;

  return (
    <FocusZone focusKey="admin" direction="column" className="gb-stack">
      <PageHead eyebrow="Studio tools" title="Admin." />

      {/* The same filter strip the catalog uses: the current section is a lit panel, not a
          lemon one, so lemon stays the colour of focus alone. */}
      <FocusZone focusKey="admin-tabs" direction="row" className="gb-row">
        <div className="gb-segmented" role="group" aria-label="Admin sections">
          {TABS.map((tab) => (
            <Focusable
              key={tab.to}
              onSelect={() => void navigate({ to: tab.to })}
              selectLabel={tab.label}
              className="gb-focusable--flat"
            >
              <button type="button" className="gb-segmented__item" aria-pressed={active === tab.to}>
                {tab.label}
              </button>
            </Focusable>
          ))}
        </div>
      </FocusZone>

      <Outlet />
    </FocusZone>
  );
}
